import React, { useMemo, useState } from 'react';
import { Button, Card, Select, Space, Typography } from 'antd';
import { LineChartOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSalesOrders } from '@/hooks/useSalesOrder';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';

const { Title, Text } = Typography;

/**
 * Entry point to open Order health check without deep-linking from a sales order detail page.
 */
const OrderHealthHubPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: orders = [], isLoading } = useSalesOrders();
  const [selectedId, setSelectedId] = useState<string | undefined>();

  const options = useMemo(
    () =>
      orders.map((o) => ({
        value: o.id,
        label: `${o.sales_order_number || o.id} — ${o.customer_name || ''}`,
      })),
    [orders]
  );

  const breadcrumbSegments = useMemo(
    () => [
      { title: 'Growth & Revenue', path: '/growth/dashboard' },
      { title: 'Customer orders', path: '/growth/orders' },
      { title: 'Order health hub' },
    ],
    []
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  return (
    <div style={{ padding: 24, maxWidth: 720 }}>
      <Title level={4}>
        <LineChartOutlined /> Order health hub
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Choose a customer order to open its 360° lifecycle, documents, and financial snapshot.
      </Text>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Select
            showSearch
            allowClear
            placeholder="Search by order number or customer"
            style={{ width: '100%' }}
            loading={isLoading}
            options={options}
            value={selectedId}
            onChange={(v) => setSelectedId(v)}
            filterOption={(input, opt) =>
              (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            optionFilterProp="label"
          />
          <Button
            type="primary"
            disabled={!selectedId}
            onClick={() => selectedId && navigate(`/growth/orders/${selectedId}/status-check`)}
          >
            Open order health check
          </Button>
        </Space>
      </Card>
    </div>
  );
};

export default OrderHealthHubPage;
